/**
 * Nutrition calculations — the single source of truth for the nutrition module
 * (menu builder, measurements, daily eating reports, public client page).
 *
 * Pure functions only: no Firebase, no Redux, no side effects. Anything that
 * needs a number computed from foods, menus or body measurements comes here,
 * so the menu builder, the PDF renderer and the Cloud Functions all agree.
 *
 * Units:
 *   - Food macro values are stored PER 100 GRAMS, except ad-hoc `customFood`
 *     items, whose values are PER SERVING (see calcIngredientNutrition).
 *   - weight kg, height cm, circumferences cm, energy kcal, macros grams.
 */

/** Macro energy density (kcal per gram). */
export const KCAL_PER_GRAM = { protein: 4, carbohydrates: 4, fat: 9 };

/** The unit that means "plain grams" — never looked up in sizeOptions. */
export const GRAM_UNIT = "גרם";

/** Units a coach can pick for an ingredient, in display order. */
export const AMOUNT_UNITS = [
  GRAM_UNIT,
  "כוס",
  "חופן",
  "יחידה קטנה",
  "יחידה בינונית",
  "יחידה גדולה",
  "כף",
  "כפית",
];

const EMPTY_TOTALS = Object.freeze({
  calories: 0,
  protein: 0,
  carbohydrates: 0,
  fat: 0,
});

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const addTotals = (target, addition, factor = 1) => {
  target.calories += addition.calories * factor;
  target.protein += addition.protein * factor;
  target.carbohydrates += addition.carbohydrates * factor;
  target.fat += addition.fat * factor;
  return target;
};

const roundTotals = (totals) => ({
  calories: Math.round(totals.calories),
  protein: Math.round(totals.protein),
  carbohydrates: Math.round(totals.carbohydrates),
  fat: Math.round(totals.fat),
});

// ============================================
// MENU NUTRITION
// ============================================

/**
 * How many grams one unit of this ingredient weighs.
 * "גרם" is always 1; every other unit must exist in the food's sizeOptions map.
 * An unknown unit yields 0 grams (and therefore 0 calories) rather than NaN.
 *
 * @param {Object} ingredient
 * @returns {number} grams per single unit
 */
export const getGramsPerUnit = (ingredient) => {
  if (!ingredient) return 0;
  if (ingredient.selectedAmount === GRAM_UNIT) return 1;
  return toNumber(ingredient.sizeOptions?.[ingredient.selectedAmount]);
};

/**
 * Nutrition of one ingredient at its chosen amount + unit, unrounded.
 * `foodType === "customFood"` items carry per-serving values, so they are
 * multiplied by the amount directly instead of scaled from 100g.
 *
 * @param {Object} ingredient
 * @returns {{calories:number, protein:number, carbohydrates:number, fat:number}}
 */
export const calcIngredientNutrition = (ingredient) => {
  if (!ingredient || !ingredient.amount || !ingredient.selectedAmount) {
    return { ...EMPTY_TOTALS };
  }

  const amount = toNumber(ingredient.amount);

  if (ingredient.foodType === "customFood") {
    return {
      calories: toNumber(ingredient.calories) * amount,
      protein: toNumber(ingredient.protein) * amount,
      carbohydrates: toNumber(ingredient.carbohydrates) * amount,
      fat: toNumber(ingredient.fat) * amount,
    };
  }

  const grams = amount * getGramsPerUnit(ingredient);
  return {
    calories: (toNumber(ingredient.calories) * grams) / 100,
    protein: (toNumber(ingredient.protein) * grams) / 100,
    carbohydrates: (toNumber(ingredient.carbohydrates) * grams) / 100,
    fat: (toNumber(ingredient.fat) * grams) / 100,
  };
};

/**
 * Nutrition an ingredient contributes to its sub-block, unrounded.
 * Connected ingredients are interchangeable substitutes, so the ingredient and
 * all of its substitutes are AVERAGED — the client eats exactly one of them.
 *
 * @param {Object} ingredient
 * @returns {{calories:number, protein:number, carbohydrates:number, fat:number}}
 */
export const calcIngredientGroupNutrition = (ingredient) => {
  if (!ingredient || !ingredient.amount || !ingredient.selectedAmount) {
    return { ...EMPTY_TOTALS };
  }

  const substitutes = ingredient.connectedIngredients || [];
  if (substitutes.length === 0) return calcIngredientNutrition(ingredient);

  const group = [ingredient, ...substitutes];
  const summed = group.reduce(
    (acc, item) => addTotals(acc, calcIngredientNutrition(item)),
    { ...EMPTY_TOTALS }
  );

  return {
    calories: summed.calories / group.length,
    protein: summed.protein / group.length,
    carbohydrates: summed.carbohydrates / group.length,
    fat: summed.fat / group.length,
  };
};

/**
 * Totals for one sub-block (one option within a meal), rounded.
 *
 * @param {Object} subBlock
 * @returns {{calories:number, protein:number, carbohydrates:number, fat:number}}
 */
export const calcSubBlockNutrition = (subBlock) => {
  const ingredients = subBlock?.ingredients || [];
  const totals = ingredients.reduce(
    (acc, ingredient) => addTotals(acc, calcIngredientGroupNutrition(ingredient)),
    { ...EMPTY_TOTALS }
  );
  return roundTotals(totals);
};

/**
 * Totals for one block (one meal), rounded.
 * Sub-blocks are alternatives, so a block's own summary averages them when the
 * menu is an "averages menu" and sums them otherwise — matching the menu-level
 * rule below.
 *
 * @param {Object} block
 * @param {boolean} [isAveragesMenu=false]
 */
export const calcBlockNutrition = (block, isAveragesMenu = false) => {
  const subBlocks = (block?.subBlocks || []).filter(
    (sb) => sb?.ingredients?.length
  );
  const totals = subBlocks.reduce(
    (acc, subBlock) => addTotals(acc, calcSubBlockNutrition(subBlock)),
    { ...EMPTY_TOTALS }
  );

  if (isAveragesMenu && subBlocks.length > 0) {
    return roundTotals({
      calories: totals.calories / subBlocks.length,
      protein: totals.protein / subBlocks.length,
      carbohydrates: totals.carbohydrates / subBlocks.length,
      fat: totals.fat / subBlocks.length,
    });
  }
  return roundTotals(totals);
};

/**
 * Totals for a whole menu, rounded.
 *
 * A normal menu sums every sub-block (the coach built one concrete day).
 * An "averages menu" (תפריט ממוצעים) lists interchangeable options, so the
 * total is divided by the number of non-empty sub-blocks in the whole menu —
 * this is the doof behavior and coaches rely on it.
 *
 * @param {Array} blocks
 * @param {boolean} [isAveragesMenu=false]
 * @returns {{calories:number, protein:number, carbohydrates:number, fat:number}}
 */
export const calcMenuNutrition = (blocks, isAveragesMenu = false) => {
  if (!Array.isArray(blocks) || blocks.length === 0) return { ...EMPTY_TOTALS };

  const totals = { ...EMPTY_TOTALS };
  let subBlocksCount = 0;

  blocks.forEach((block) => {
    (block?.subBlocks || []).forEach((subBlock) => {
      if (!subBlock?.ingredients?.length) return;
      addTotals(totals, calcSubBlockNutrition(subBlock));
      subBlocksCount += 1;
    });
  });

  if (isAveragesMenu && subBlocksCount > 0) {
    return roundTotals({
      calories: totals.calories / subBlocksCount,
      protein: totals.protein / subBlocksCount,
      carbohydrates: totals.carbohydrates / subBlocksCount,
      fat: totals.fat / subBlocksCount,
    });
  }
  return roundTotals(totals);
};

/**
 * Sum arbitrary macro objects — used for a day's eating-report entries.
 *
 * @param {Array<Object>} items objects carrying calories/protein/carbohydrates/fat
 */
export const sumNutrition = (items) => {
  const totals = (items || []).reduce(
    (acc, item) =>
      addTotals(acc, {
        calories: toNumber(item?.calories),
        protein: toNumber(item?.protein),
        carbohydrates: toNumber(item?.carbohydrates),
        fat: toNumber(item?.fat),
      }),
    { ...EMPTY_TOTALS }
  );
  return roundTotals(totals);
};

// ============================================
// BODY METRICS
// ============================================

/**
 * BMI = kg / m².
 * @returns {number|null} rounded to 1 decimal, or null when inputs are missing
 */
export const calcBmi = ({ weight, height }) => {
  const kg = toNumber(weight);
  const cm = toNumber(height);
  if (kg <= 0 || cm <= 0) return null;
  return Math.round((kg / (cm / 100) ** 2) * 10) / 10;
};

/**
 * BMR — Mifflin-St Jeor (1990), the current clinical default.
 * NOTE: doof labelled its BMR "Mifflin-St Jeor" but implemented the 1919
 * Harris-Benedict coefficients; this is the corrected formula. Harris-Benedict
 * is still available as calcRmr for coaches who compare against old numbers.
 *
 * @param {{weight:number, height:number, age:number, gender:"male"|"female"}} p
 * @returns {number|null} kcal/day, rounded
 */
export const calcBmr = ({ weight, height, age, gender }) => {
  const kg = toNumber(weight);
  const cm = toNumber(height);
  const years = toNumber(age);
  if (kg <= 0 || cm <= 0 || years <= 0) return null;
  if (gender !== "male" && gender !== "female") return null;

  const base = 10 * kg + 6.25 * cm - 5 * years;
  return Math.round(gender === "male" ? base + 5 : base - 161);
};

/**
 * RMR — Harris-Benedict (1919 revision). Kept because doof reported it beside
 * BMR and coaches recognise the numbers.
 *
 * @returns {number|null} kcal/day, rounded
 */
export const calcRmr = ({ weight, height, age, gender }) => {
  const kg = toNumber(weight);
  const cm = toNumber(height);
  const years = toNumber(age);
  if (kg <= 0 || cm <= 0 || years <= 0) return null;
  if (gender !== "male" && gender !== "female") return null;

  return Math.round(
    gender === "male"
      ? 66.5 + 13.75 * kg + 5.003 * cm - 6.75 * years
      : 655.1 + 9.563 * kg + 1.85 * cm - 4.676 * years
  );
};

/** Activity multipliers for TDEE, keyed by the patient's activityLevel. */
export const ACTIVITY_MULTIPLIERS = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  active: 1.725,
  very_active: 1.9,
};

/** Hebrew labels for the activity levels above. */
export const ACTIVITY_LEVEL_LABELS = {
  sedentary: "יושבני (ללא פעילות)",
  light: "פעילות קלה (1-3 אימונים בשבוע)",
  moderate: "פעילות בינונית (3-5 אימונים בשבוע)",
  active: "פעילות גבוהה (6-7 אימונים בשבוע)",
  very_active: "פעילות גבוהה מאוד (אימונים יומיים אינטנסיביים)",
};

/**
 * TDEE = BMR × activity multiplier. doof collected activityLevel but never
 * used it; the coach still overrides the calorie goal manually.
 *
 * @param {number} bmr
 * @param {string} activityLevel key of ACTIVITY_MULTIPLIERS
 * @returns {number|null} kcal/day, rounded
 */
export const calcTdee = (bmr, activityLevel) => {
  const base = toNumber(bmr);
  const multiplier = ACTIVITY_MULTIPLIERS[activityLevel];
  if (base <= 0 || !multiplier) return null;
  return Math.round(base * multiplier);
};

/** Short activity names for inline explanations ("× פעילות בינונית (1.55)"). */
export const ACTIVITY_LEVEL_SHORT_LABELS = {
  sedentary: "יושבני",
  light: "פעילות קלה",
  moderate: "פעילות בינונית",
  active: "פעילות גבוהה",
  very_active: "פעילות גבוהה מאוד",
};

// ============================================
// MANUAL OVERRIDES OF DERIVED METRICS (Round 3 / Phase 20)
// ============================================

/**
 * Derived metrics the coach may enter by hand on a measurement. The value the
 * app reads (`bmi` / `bmr`) is always the effective one; when it is manual the
 * row also carries `<key>Manual: true` and `<key>Computed: <number|null>` so
 * the computed value is never lost. Body fat is not listed here on purpose:
 * a manual body-fat value IS the measured `fatPercentage` field (it already
 * beats the US-Navy estimate), so the same "measured wins" rule covers it.
 */
export const METRIC_OVERRIDE_FIELDS = {
  bmi: { min: 5, max: 100, label: "BMI", unit: "", digits: 1 },
  bmr: { min: 300, max: 10000, label: "BMR", unit: "קק״ל ליום", digits: 0 },
};

/**
 * Normalize a raw overrides map ({ bmi: "27.4", bmr: "" }) into numbers.
 * Empty / null / undefined → null (no override). Out-of-range → an error
 * string so the caller can show it in Hebrew.
 *
 * @returns {{values: {bmi:number|null, bmr:number|null}, errors: string[]}}
 */
export const sanitizeMetricOverrides = (raw = {}) => {
  const values = {};
  const errors = [];
  Object.entries(METRIC_OVERRIDE_FIELDS).forEach(([key, spec]) => {
    const input = raw?.[key];
    if (input === undefined || input === null || input === "") {
      values[key] = null;
      return;
    }
    const num = Number(input);
    if (!Number.isFinite(num)) {
      errors.push(`${spec.label}: ערך לא תקין`);
      values[key] = null;
      return;
    }
    if (num < spec.min || num > spec.max) {
      errors.push(`${spec.label} חייב להיות בין ${spec.min} ל-${spec.max}`);
      values[key] = null;
      return;
    }
    values[key] = Math.round(num * 10 ** spec.digits) / 10 ** spec.digits;
  });
  return { values, errors };
};

// ============================================
// SUGGESTED DAILY GOALS (Round 3 / Phase 20)
// ============================================

/**
 * What the client is trying to do with their weight. Drives the calorie
 * suggestion only: TDEE + delta. Stored as `patient.nutrition.goalType`.
 */
export const GOAL_TYPES = {
  lose: { label: "ירידה במשקל", delta: -500, explain: "מינוס 500 ליעד ירידה במשקל" },
  maintain: { label: "שמירה על המשקל", delta: 0, explain: "" },
  gain: { label: "עלייה במשקל / מסת שריר", delta: 300, explain: "פלוס 300 ליעד עלייה" },
};

export const DEFAULT_GOAL_TYPE = "maintain";

/** Protein per kg of body weight when no diet plan dictates a ratio. */
const PROTEIN_G_PER_KG = 1.6;
/** Fat share of calories when no diet plan dictates a ratio. */
const DEFAULT_FAT_SHARE = 0.3;

const fmtInt = (n) => Math.round(n).toLocaleString("en-US");

/**
 * Suggest the four daily goals as helper text — never auto-filled, never
 * persisted. Every suggestion carries the arithmetic behind it so the coach
 * can judge it.
 *
 * Inputs (all optional; a missing one yields `{ missing: <hebrew> }` for the
 * suggestions that need it):
 *   bmr            effective BMR (manual override already applied)
 *   activityLevel  key of ACTIVITY_MULTIPLIERS
 *   weight         kg
 *   goalType       key of GOAL_TYPES (defaults to maintain)
 *   dietPlan       key of DIET_PLANS — when set, macros follow its ratios
 *   caloriesEntered the calorie goal the coach already typed; macros are
 *                  split from it when present, else from the suggested calories
 *
 * @returns {{calories, protein, carbohydrates, fat}} each either
 *   { value:number, unit:string, explain:string } or { missing:string }
 */
export const suggestDailyGoals = ({
  bmr,
  activityLevel,
  weight,
  goalType = DEFAULT_GOAL_TYPE,
  dietPlan,
  caloriesEntered,
} = {}) => {
  const base = toNumber(bmr);
  const multiplier = ACTIVITY_MULTIPLIERS[activityLevel];
  const kg = toNumber(weight);
  const goal = GOAL_TYPES[goalType] || GOAL_TYPES[DEFAULT_GOAL_TYPE];

  const missingCalories = [];
  if (base <= 0) missingCalories.push("משקל, גובה, מגדר ותאריך לידה");
  if (!multiplier) missingCalories.push("רמת פעילות");

  let calories;
  if (missingCalories.length > 0) {
    calories = { missing: `הוסיפו ${missingCalories.join(" ו")} כדי לקבל המלצה` };
  } else {
    const tdee = Math.round(base * multiplier);
    const value = Math.max(0, tdee + goal.delta);
    const levelLabel = ACTIVITY_LEVEL_SHORT_LABELS[activityLevel] || activityLevel;
    const parts = [`BMR ${fmtInt(base)} × ${levelLabel} (${multiplier}) = ${fmtInt(tdee)}`];
    if (goal.explain) parts.push(goal.explain);
    calories = { value, unit: "קק״ל", explain: parts.join(", ") };
  }

  // Macros split from the calories the coach typed (preferred) or suggested.
  const typed = toNumber(caloriesEntered);
  const calorieBase = typed > 0 ? typed : calories.value || 0;
  const calorieSource = typed > 0 ? `מתוך ${fmtInt(typed)} קק״ל שהוזנו` : `מתוך ${fmtInt(calorieBase)} קק״ל מומלצים`;
  const plan = getDietPlan(dietPlan);

  if (plan && plan.protein !== null && calorieBase > 0) {
    const pct = (share) => `${Math.round(share * 100)}%`;
    return {
      calories,
      protein: {
        value: Math.round((calorieBase * plan.protein) / KCAL_PER_GRAM.protein),
        unit: "ג׳",
        explain: `${pct(plan.protein)} מהקלוריות ÷ 4, ${calorieSource}`,
      },
      carbohydrates: {
        value: Math.round((calorieBase * plan.carbohydrates) / KCAL_PER_GRAM.carbohydrates),
        unit: "ג׳",
        explain: `${pct(plan.carbohydrates)} מהקלוריות ÷ 4, ${calorieSource}`,
      },
      fat: {
        value: Math.round((calorieBase * plan.fat) / KCAL_PER_GRAM.fat),
        unit: "ג׳",
        explain: `${pct(plan.fat)} מהקלוריות ÷ 9, ${calorieSource}`,
      },
    };
  }

  const protein =
    kg > 0
      ? {
          value: Math.round(kg * PROTEIN_G_PER_KG),
          unit: "ג׳",
          explain: `${PROTEIN_G_PER_KG} ג׳ × ${kg} ק״ג משקל גוף`,
        }
      : { missing: "הוסיפו משקל כדי לקבל המלצה" };

  if (calorieBase <= 0) {
    const missing = { missing: "הוסיפו יעד קלוריות (או את הנתונים לחישובו) כדי לקבל המלצה" };
    return { calories, protein, carbohydrates: missing, fat: missing };
  }

  const fatGrams = Math.round((calorieBase * DEFAULT_FAT_SHARE) / KCAL_PER_GRAM.fat);
  const proteinKcal = protein.value ? protein.value * KCAL_PER_GRAM.protein : 0;
  const carbsKcal = Math.max(0, calorieBase - proteinKcal - fatGrams * KCAL_PER_GRAM.fat);
  const carbsGrams = Math.round(carbsKcal / KCAL_PER_GRAM.carbohydrates);

  return {
    calories,
    protein,
    carbohydrates: {
      value: carbsGrams,
      unit: "ג׳",
      explain: `מה שנשאר אחרי חלבון ושומן (${fmtInt(carbsKcal)} קק״ל) ÷ 4, ${calorieSource}`,
    },
    fat: {
      value: fatGrams,
      unit: "ג׳",
      explain: `${Math.round(DEFAULT_FAT_SHARE * 100)}% מהקלוריות ÷ 9, ${calorieSource}`,
    },
  };
};

/**
 * Body fat % — US-Navy-style formula without a waist term, as used by doof.
 * Returns null (never throws) when a measurement is missing, because every
 * measurement field is optional.
 *
 * @param {{height:number, abdomen:number, neck:number}} m
 * @param {"male"|"female"} gender
 * @returns {number|null} percent, rounded to 1 decimal
 */
export const calcBodyFat = ({ height, abdomen, neck }, gender) => {
  const cm = toNumber(height);
  const abdomenCm = toNumber(abdomen);
  const neckCm = toNumber(neck);
  if (cm <= 0 || abdomenCm <= 0 || neckCm <= 0) return null;
  if (gender !== "male" && gender !== "female") return null;

  const base = gender === "male" ? 10.1 : 19.2;
  const value = base - 0.239 * cm + 0.8 * abdomenCm - 0.5 * neckCm;
  return Math.round(value * 10) / 10;
};

// ============================================
// DIET PLANS / MACRO GOALS
// ============================================

/**
 * Macro splits offered to the coach. `key` is what we store; `label` is what
 * the coach sees. Percentages are of total daily calories.
 */
export const DIET_PLANS = [
  {
    key: "balanced",
    label: "דיאטה מאוזנת — 20% חלבון, 30% שומן, 50% פחמימה",
    protein: 0.2,
    fat: 0.3,
    carbohydrates: 0.5,
  },
  {
    key: "reduced_carbs",
    label: "דיאטה מופחתת פחמימות — 30% חלבון, 30% שומן, 40% פחמימה",
    protein: 0.3,
    fat: 0.3,
    carbohydrates: 0.4,
  },
  {
    key: "low_carbs",
    label: "דיאטה דלת פחמימות — 40% חלבון, 35% שומן, 25% פחמימה",
    protein: 0.4,
    fat: 0.35,
    carbohydrates: 0.25,
  },
  {
    key: "keto",
    label: "דיאטה קטוגנית — 25% חלבון, 70% שומן, 5% פחמימה",
    protein: 0.25,
    fat: 0.7,
    carbohydrates: 0.05,
  },
  {
    key: "low_fat",
    label: "דיאטה דלת שומן — 25% חלבון, 25% שומן, 50% פחמימה",
    protein: 0.25,
    fat: 0.25,
    carbohydrates: 0.5,
  },
  { key: "none", label: "ללא תוכנית תזונה", protein: null, fat: null, carbohydrates: null },
];

export const getDietPlan = (planKey) =>
  DIET_PLANS.find((plan) => plan.key === planKey) || null;

/**
 * Daily macro grams implied by a calorie goal + diet plan.
 *
 * @param {number} calorieGoal kcal/day
 * @param {string} planKey key from DIET_PLANS
 * @returns {{protein:number, carbohydrates:number, fat:number}|null}
 */
export const macrosFromDietPlan = (calorieGoal, planKey) => {
  const calories = toNumber(calorieGoal);
  const plan = getDietPlan(planKey);
  if (calories <= 0 || !plan || plan.protein === null) return null;

  return {
    protein: Math.round((calories * plan.protein) / KCAL_PER_GRAM.protein),
    carbohydrates: Math.round(
      (calories * plan.carbohydrates) / KCAL_PER_GRAM.carbohydrates
    ),
    fat: Math.round((calories * plan.fat) / KCAL_PER_GRAM.fat),
  };
};

// ============================================
// GOAL PROGRESS (shared by the stats bar, the report tab and the public page)
// ============================================

/** Below this share of the goal the value counts as "under" (doof semantics). */
export const GOAL_ON_TARGET_FLOOR = 0.8;

/**
 * Compare an actual value against a goal.
 * `status` drives the shared color tokens: "under" | "on_target" | "over".
 *
 * Thresholds match what nutrition coaches are used to from doof: under 80% of
 * the goal is "under", 80-100% is on target, anything above the goal is "over".
 *
 * `percent` is the true (uncapped) percentage for the number display;
 * `barPercent` is clamped to 0-100 for the progress bar width. doof used the
 * clamped value for both, which made an over-goal bar unable to turn red.
 *
 * @param {number} actual
 * @param {number} goal
 * @returns {{percent:number, barPercent:number, status:string, hasGoal:boolean}}
 */
export const calcGoalProgress = (actual, goal) => {
  const value = toNumber(actual);
  const target = toNumber(goal);
  if (target <= 0) {
    return { percent: 0, barPercent: 0, status: "no_goal", hasGoal: false };
  }

  const ratio = value / target;
  let status = "on_target";
  if (ratio < GOAL_ON_TARGET_FLOOR) status = "under";
  else if (ratio > 1) status = "over";

  return {
    percent: Math.round(ratio * 100),
    barPercent: Math.min(Math.max(Math.round(ratio * 100), 0), 100),
    status,
    hasGoal: true,
  };
};

/**
 * Age in whole years from a birth date, for BMR/RMR.
 * Accepts a Date, a Firestore Timestamp, or anything Date can parse.
 *
 * @returns {number|null}
 */
export const calcAgeFromBirthDate = (birthDate) => {
  if (!birthDate) return null;
  const date =
    typeof birthDate?.toDate === "function" ? birthDate.toDate() : new Date(birthDate);
  if (Number.isNaN(date.getTime())) return null;

  const now = new Date();
  let age = now.getFullYear() - date.getFullYear();
  const monthDiff = now.getMonth() - date.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < date.getDate())) age -= 1;
  return age >= 0 ? age : null;
};
